import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { Button, type ButtonProps } from '@/ux-common/components/button/button';
import { cn } from '@/ux-common/lib/utils';

/**
 * DropdownButton — Minimal React wrapper for a button that opens a menu.
 *
 * Composes the Button atom with a chevron. All visual styling is delivered
 * through CSS classes applied via `className`:
 *
 * - Variant:  .btn-default, .btn-tier2, .btn-tier3, .btn-brand,
 *             .btn-destructive, .btn-link, .btn-inverse, .btn-transparent
 * - Size:     .btn-xs, .btn-sm, .btn-md, .btn-lg
 * - Shape:    .btn-rounded, .btn-round, .btn-circle, .btn-square
 *
 * The wrapper provides:
 * - Ref forwarding (via the underlying Button atom)
 * - `type="button"` default (inherited from Button)
 * - `data-component="DropdownButton"` for identification
 * - Default `aria-haspopup="menu"` (overridden by Radix / consumer if supplied)
 * - `aria-expanded` mirrored from `triggered` for non-Radix open-state control
 * - Conditional `<span>` wrapping the label for truncation support
 * - A chevron that rotates 180deg when the button has either
 *   `[data-state="open"]` (Radix `asChild`) or `[aria-expanded="true"]`
 *   (manual `triggered`)
 *
 * Note: children is a required slot for text triggers. For icon-only usage
 * (square/circle shape with no children), supply `aria-label` for accessibility.
 *
 * @example
 * ```tsx
 * // Basic dropdown trigger
 * <DropdownButton className="btn-tier3">Sort By</DropdownButton>
 *
 * // With Radix DropdownMenuTrigger — auto state sync via data-state
 * <DropdownMenuTrigger asChild>
 *   <DropdownButton className="btn-tier3 btn-sm">Menu</DropdownButton>
 * </DropdownMenuTrigger>
 *
 * // Icon-only (requires aria-label)
 * <DropdownButton
 *   className="btn-tier2 btn-lg btn-square"
 *   aria-label="Open filters"
 *   triggered={isOpen}
 * />
 *
 * // Controlled triggered (non-Radix)
 * <DropdownButton className="btn-tier2" triggered={isOpen}>
 *   Advanced
 * </DropdownButton>
 * ```
 */

export interface DropdownButtonProps extends ButtonProps {
  /**
   * When defined, mirrors to `aria-expanded` on the rendered button. Use for
   * standalone open-state control when NOT composed inside a Radix trigger.
   * Leave undefined when used with `DropdownMenuTrigger asChild` / similar;
   * Radix sets `data-state="open"` automatically, which also rotates the chevron.
   */
  triggered?: boolean;
}

export const DropdownButton = React.forwardRef<HTMLButtonElement, DropdownButtonProps>(
  ({ className, children, triggered, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        data-component="DropdownButton"
        aria-haspopup="menu"
        aria-expanded={triggered}
        className={cn('btn', className, 'dropdown-button')}
        {...props}
      >
        {children != null && <span className="dropdown-button-label">{children}</span>}
        <span className="btn-icon dropdown-button-chevron" aria-hidden="true">
          <ChevronDown />
        </span>
      </Button>
    );
  }
);

DropdownButton.displayName = 'DropdownButton';
